import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { capitalize } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { DataListItem } from '@node-space/ui-components'
import { Country } from 'types/authentication/Profile'
import { IndividualCustomerType } from 'types/customers'
import { getNestedCustomerAddress } from '../utils'

interface IndividualCustomerSidePanelViewProps {
  customer: IndividualCustomerType
  countries: Country[]
  isCountriesLoading: boolean
  isCountriesLoadingError: boolean
}

export const IndividualCustomerSidePanelView = ({
  customer,
  countries,
  isCountriesLoading,
  isCountriesLoadingError,
}: IndividualCustomerSidePanelViewProps) => {
  const { t } = useTranslation()

  const { details, person } = customer?.individual || {}
  const { birthCountryCode, documentNumber } = details
  const { firstName, lastName, dateOfBirth, address } = person
  const { countryCode } = address || {}
  const customerAddress = getNestedCustomerAddress(customer?.individual?.person?.address)

  const nationalityCountry = useMemo(() => {
    return countries?.find(country => {
      if (country?.code === birthCountryCode) {
        return country
      }
    })
  }, [countries, customer])

  const residenceCountry = useMemo(() => {
    return countries?.find(country => {
      if (country?.code === countryCode) {
        return country
      }
    })
  }, [countries, customer])

  return (
    <>
      {customer?.type && (
        <DataListItem
          id="type"
          testId="type"
          title={t('customers.type')}
          body={capitalize(customer?.type?.toLowerCase())}
        />
      )}
      {firstName && (
        <DataListItem id="first-name" testId="first-name" title={t('firstName')} body={firstName} />
      )}

      {lastName && (
        <DataListItem id="last-name" testId="last-name" title={t('lastName')} body={lastName} />
      )}

      {documentNumber && (
        <DataListItem
          id="document-number"
          testId="document-number"
          title={t('customers.personalIdNumber')}
          body={documentNumber}
        />
      )}

      {dateOfBirth && (
        <DataListItem
          id="date-of-birth"
          testId="date-of-birth"
          title={t('formFields.dateOfBirth')}
          body={dayjs(dateOfBirth).format('DD-MM-YYYY')}
        />
      )}

      {birthCountryCode &&
        nationalityCountry &&
        !isCountriesLoadingError &&
        !isCountriesLoading && (
          <DataListItem
            id="nationality-country"
            testId="nationality-country"
            title={t('customers.countryOfNationality')}
            body={nationalityCountry?.name || ''}
          />
        )}

      <h4 className="text-h4 text-foreground-prominent">{t('customers.residentialAddress')}</h4>
      {customerAddress && (
        <DataListItem
          id="customer-address"
          testId="customer-address"
          title={t('customers.address.address')}
          body={customerAddress}
        />
      )}
      {countryCode && residenceCountry && (
        <DataListItem
          id="residence-country"
          testId="residence-country"
          title={t('customers.country')}
          body={residenceCountry?.name}
        />
      )}
    </>
  )
}
